import React from 'react'
import { DocumentPage, Text, View, StyleSheet, useStyle } from '~/lib/pdf'
import Moment from 'moment'

const isNotBlank = (item) => ![null, undefined, ''].includes(item)

export const defaultStyle = {
  xMargin: 0.4,
  yMargin: 0.4,
  size: 'Letter',
  accentColor: '#2F5496',
  itemHeadingBackgroundColor: '#E7E6E6',
  lineHeightMultiplier: 1.0,
  listLineHeightMultiplier: 1.0,
  primaryFontFamily: 'Carlito',
  secondaryFontFamily: 'Carlito',
  headingFontSize: 22,
  bodyFontSize: 11,
  summaryHeading: 'Professional Summary',
  workHeading: 'Professional Experience',
  educationHeading: 'Education',
  snapshotHeading: 'Snapshot of Results',
  presentDateField: 'Present',
}

export const getOptionalEditorFields = () => ({
  basicsJobTitle: true,
  basicsLocation: true,
  basicsSummary: true,
  basicsWebsite: false,
  basicsLinkedin: true,
  volunteer: false,
  workLocation: true,
  workSummary: true,
  educationCourses: false,
  educationLocation: false,
  educationGpa: false,
  educationTenure: false,
  skillProficiency: false,
  additionalInfo: false,
  awards: false,
})

export const Template = ({ jsonDocument, templateStyle, title }) => {
  const {
    bodyFontSize,
    headingFontSize,
    itemHeadingBackgroundColor,
    accentColor,
    lineHeightMultiplier,
    summaryHeading,
  } = templateStyle
  const itemHeadingFontSize = (bodyFontSize * 12) / 10
  const styles = StyleSheet.create({
    heading: {
      fontWeight: 'bold',
      fontSize: headingFontSize,
    },
    body: {
      fontSize: bodyFontSize,
    },
    contactSection: {
      flexDirection: 'row',
      fontSize: bodyFontSize,
      paddingBottom: '4px',
    },
    sectionTitle: {
      fontSize: bodyFontSize,
      fontWeight: 'bold',
      borderBottom: '1px solid #000000',
      marginBottom: '3px',
    },
    itemHeading: {
      fontSize: itemHeadingFontSize,
      backgroundColor: itemHeadingBackgroundColor,
      color: accentColor,
      padding: '1px 0',
      marginBottom: '2px',
      borderBottom: '1px solid #000000',
      fontWeight: 'bold',
    },
    sectionHeading: {
      fontSize: itemHeadingFontSize,
      marginBottom: '4px',
      paddingBottom: '2px',
      borderBottom: '1px solid #000000',
      fontWeight: 'bold',
    },
    professionalSummary: {
      marginBottom: '4px',
      lineHeight: `${bodyFontSize * lineHeightMultiplier}px`,
    },
  })

  const { basics, education, work, skills } = jsonDocument
  const { name, email, phone, location, label, profiles, summary } =
    basics || {}
  const { city, region, postalCode } = location || {}
  const linkedinProfile =
    profiles &&
    profiles.find((element) => {
      return element.network?.toLowerCase() === 'linkedin'
    })

  const linkedinProfileUrl =
    linkedinProfile && linkedinProfile.url?.replace(/https{0,1}:\/\//, '')

  const regionPostalCode = [region, postalCode].filter(isNotBlank).join(' ')
  const cityStateZip = city ? `${city}, ${regionPostalCode}` : regionPostalCode
  const contactItems = [phone, email, linkedinProfileUrl, cityStateZip]
    .filter(isNotBlank)
    .join(' | ')

  return (
    <DocumentPage templateStyle={templateStyle} title={title}>
      <Text style={styles.heading}>{name}</Text>
      <Text style={styles.itemHeading}>{label}</Text>
      <View style={styles.contactSection}>
        <Text>{contactItems}</Text>
      </View>
      {summary && (
        <View style={{ marginBottom: '4px' }}>
          <Text style={styles.sectionHeading}>{summaryHeading}</Text>
          <Text style={styles.body}>{summary}</Text>
        </View>
      )}
      {skills && skills.length > 0 && <SkillSection skills={skills} />}
      <View>
        {work && work.length > 0 && <WorkSection work={work} styles={styles} />}
        {education && education.length > 0 && (
          <EducationSection education={education} styles={styles} />
        )}
      </View>
    </DocumentPage>
  )
}

const EducationSection = ({ education, styles }) => {
  const {
    templateStyle: { educationHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{educationHeading}</Text>
      {education.map((edItem, index) => (
        <EducationView education={edItem} key={`education-${index}`} />
      ))}
    </>
  )
}

const WorkSection = ({ work, styles }) => {
  const {
    templateStyle: { workHeading },
  } = useStyle()
  return (
    <>
      <Text style={styles.sectionTitle}>{workHeading}</Text>
      {work.map((job, index) => (
        <JobView job={job} key={`job-${index}`} />
      ))}
    </>
  )
}

const SkillSection = ({ skills }) => {
  return (
    <View style={{ marginBottom: '4px' }}>
      {skills?.map((skill, index) => (
        <SkillView skill={skill} key={`skill-${index}`} />
      ))}
    </View>
  )
}

const SkillView = ({ skill }) => {
  const {
    templateStyle: { accentColor, bodyFontSize, lineHeightMultiplier },
  } = useStyle()
  const styles = StyleSheet.create({
    container: {
      fontSize: bodyFontSize,
      paddingBottom: '4px',
    },
    skillName: {
      fontWeight: 'bold',
      paddingRight: '4px',
      color: accentColor,
    },
    skillList: {
      textOverflow: 'wrap',
      lineHeight: `${bodyFontSize * lineHeightMultiplier}px`,
    },
  })
  if (!skill.name) {
    return null
  }
  return (
    <View style={styles.container}>
      <View style={styles.skillName}>
        <Text>{skill.name}</Text>
      </View>
      <View style={styles.skillList}>
        <Text>{skill.keywords?.join(', ')}</Text>
      </View>
    </View>
  )
}

const JobView = ({ job }) => {
  const {
    templateStyle: {
      bodyFontSize,
      accentColor,
      itemHeadingBackgroundColor,
      lineHeightMultiplier,
      snapshotHeading,
    },
  } = useStyle()
  const styles = StyleSheet.create({
    jobContainer: {
      fontSize: bodyFontSize,
      paddingBottom: '15px',
    },
    jobRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      color: accentColor,
      backgroundColor: itemHeadingBackgroundColor,
      fontWeight: 'bold',
    },
    jobSummary: {
      lineHeight: `${bodyFontSize * lineHeightMultiplier}px`,
      paddingBottom: '2px',
    },
    jobHeading: {
      flexDirection: 'row',
      fontWeight: 'bold',
      padding: '2px 0',
    },
    jobTenure: { flexDirection: 'row', alignItems: 'center' },
    subHeading: { color: accentColor, fontWeight: 'bold', fontStyle: 'italic' },
  })
  if (!job.position && !job.name) {
    return null
  }
  const jobHeading = [job.name, job.position, job.location]
    .filter(isNotBlank)
    .join(' | ')

  return (
    <View style={styles.jobContainer} wrap={false}>
      <View style={styles.jobRow}>
        <View style={styles.jobHeading}>
          <Text>{jobHeading}</Text>
        </View>
        <View style={styles.jobTenure}>
          <Text>
            <Tenure item={job} />
          </Text>
        </View>
      </View>
      <View style={styles.jobSummary}>
        <Text>{job.summary}</Text>
      </View>
      <View style={styles.subHeading}>
        {job.highlights?.length > 0 && <Text>{snapshotHeading}</Text>}
      </View>
      <View>
        {job.highlights &&
          job.highlights.map((highlight, index) => (
            <BulletLine key={`highlight-${index}`}>{highlight}</BulletLine>
          ))}
      </View>
    </View>
  )
}

const EducationView = ({ education }) => {
  const {
    templateStyle: { lineHeightMultiplier, bodyFontSize },
  } = useStyle()
  const styles = StyleSheet.create({
    container: {
      paddingBottom: `${2 * lineHeightMultiplier}px`,
      fontSize: bodyFontSize,
    },
    degree: {
      fontWeight: 'bold',
      paddingRight: '8px',
    },
    educationRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      marginBottom: '3px',
    },
  })

  if (!education.institution && !education.studyType) {
    return null
  }

  const { institution, area, studyType } = education

  const degreeConcentration = [studyType, area].filter(isNotBlank).join(', ')

  return (
    <View style={styles.container} wrap={false}>
      <View style={styles.educationRow}>
        <Text style={styles.degree}>{degreeConcentration}</Text>
        <Text>{institution}</Text>
      </View>
    </View>
  )
}
const BulletLine = ({ children }) => {
  const {
    templateStyle: { listLineHeightMultiplier },
  } = useStyle()

  const styles = StyleSheet.create({
    highlightBullet: {
      padding: '0 8px',
    },
    highlightText: {
      lineHeight: `${10 * listLineHeightMultiplier}px`,
    },
    container: { flexDirection: 'row', paddingRight: '20px' },
  })

  const hcbNone = (word) => [word]

  return (
    <View style={styles.container}>
      <Text style={styles.highlightBullet}>&#8226;</Text>
      <Text hyphenationCallback={hcbNone} style={styles.highlightText}>
        {children}
      </Text>
    </View>
  )
}

const momentFormat = (date, nullValue = '') => {
  if (!date || [undefined, null, ''].includes(date)) {
    return nullValue
  }
  try {
    const result = Moment(date).format('MM/YYYY')
    if (result === 'Invalid date') return nullValue
    return result
  } catch {
    return nullValue
  }
}

const Tenure = ({ item }) => {
  const {
    templateStyle: { presentDateField },
  } = useStyle()

  if (!item.startDate && !item.endDate) {
    return null
  }

  const formattedStart = momentFormat(item.startDate)
  const formattedEnd = momentFormat(item.endDate, presentDateField)

  return [formattedStart, formattedEnd].filter(isNotBlank).join(' - ')
}
